import React, { useContext, useEffect, useState } from 'react';
import { Amplitude, LogOnMount } from 'react-amplitude-hooks';
import Big from 'big.js';
import { AuthContext } from 'States/auth/authState';
import i18nextTranslate from 'Lang/i18nextTranslate';
import { i18nextKeys } from 'Lang/i18nextKeys';
import { UiContext } from 'States/ui/uiState';
import { ConfigContext } from 'States/config/configState';
import { isTenantFeatureAvailable } from 'Helpers/toggleFeatures';
import useFeatureAvailability from 'Hooks/useFeatureAvailability';
import useKycTierCheck from 'Hooks/useKycTierCheck';
import CustomFooter from 'Components/shared/Custom/CustomFooter';
import InvestmentCard from './InvestmentCard';
import api from 'Api';
import useHandleError from 'Utils/handleError';
import { TENANT_FEATURE } from 'Enums';
import { ORDER_STATUS } from 'Enums/OrderStatus';
import { COLLECT_METHOD } from 'Enums/CollectMethod';
import { EnvContext } from 'States/env/envState';
import LoadingSpinner from 'Components/shared/LoadingSpinner';
import EmptyState from './EmptyState';

const Investments = () => {
  const {
    breakpoints: { sm }
  } = useContext(UiContext);
  const {
    isAuthenticated,
    user: {
      profile: { sub },
    }
  } = useContext(AuthContext);
  const [assets, setAssets] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const { env } = useContext(EnvContext);
  const { data: features } = useFeatureAvailability.query({
    queryFnArgs: !isAuthenticated ? [env?.TenantId] : [],
    enabled: !!env,
    staleTime: 300000,
  });
  const {
    config: { kycTierFunctionalRestrictions },
  } = useContext(ConfigContext);
  const handleError = useHandleError();
  const { isUserTierSufficient: isRedemptionTierReached } = useKycTierCheck(
    kycTierFunctionalRestrictions?.visibility?.redemption
  );
  const isRedemptionAvailable = isTenantFeatureAvailable(features, TENANT_FEATURE.redeem);

  const GRID_ITEM_WIDTH = 260;
  const GRID_GAP = 40;

  useEffect(() => {
    const compileAssetsList = async () => {
      const fetchBalance = async ({ id, address }) => {
        try {
          return await api.Wallets.getBalance(id, address);
        } catch (error) {
          if (error.response?.status === 404) {
            return null;
          }
          handleError({ error });
        }
      };
      try {
        const { value: sentOrders } = await api.PurchaseOrder.list({
          expand: "PurchasedAssets",
          filter: `UserId eq ${sub} and Status eq '${ORDER_STATUS.Sent}' and CollectMethod eq '${COLLECT_METHOD.OwnWallet}'`
        });
        const uniquePurchasedAssets = [...new Map(
          sentOrders.flatMap(order =>
            order.PurchasedAssets.map(purchasedAsset => {
              const id = purchasedAsset.UniqueAssetId;
              const address = order.TargetEthAddress;
              return [ `${id}-${address}`, { id, address } ];
            })
          )
        ).values()];
        if (!uniquePurchasedAssets?.length) {
          setLoading(false);
          return;
        }
        const balances = await Promise.all(uniquePurchasedAssets.map(
          async (uniquePurchasedAsset) => fetchBalance(uniquePurchasedAsset)
        ));
        const assetIds = new Set();
        balances.forEach((balance) => {
          if (!balance || Big(balance.Amount).eq(0)) {
            return;
          }
          assetIds.add(`0x${balance.UniqueAssetId}`);
        });
        if (!assetIds.size) {
          setLoading(false);
          return;
        }
        const assetIdsQuery = [...assetIds].map(id => `'${id}'`).join();
        const { value: assets } = await api.Assets.getAssetsConfig({
          tenantId: env?.TenantId,
          filter: `Id in (${assetIdsQuery})`
        });
        if (!isRedemptionAvailable || !isRedemptionTierReached || !assets?.length) {
          setAssets(assets);
          setLoading(false);
          return;
        }
        const { value: redemptionMethodsConfig } = await api.Redemptions.getMethodsConfig({
          filter: `Enabled and UserPays/UniqueAssetId	in (${assetIdsQuery})`
        });
        if (!redemptionMethodsConfig?.length) {
          setAssets(assets);
          setLoading(false);
          return;
        }
        const assetsWithRedemptionConfig = assets.map(asset => ({
          ...asset,
          redemptionConfig: redemptionMethodsConfig.find(
            redemptionConfig => redemptionConfig.UserPays.UniqueAssetId === asset.Id
          )
        }));
        setAssets(assetsWithRedemptionConfig);
        setLoading(false);
      } catch (error) {
        handleError({ error });
      }
    }
    compileAssetsList();
  }, []);

  return (
    <Amplitude
      eventProperties={{
        scope: 'purchase',
      }}
    >
      <LogOnMount eventType="Shop opened">
        <div
          data-qa="purchase"
          className="h-full flex flex-col justify-between"
        >
          <div className='flex flex-col'>
            <div className="relative flex items-center justify-between md:block mb-20 px-20 md:px-0">
              <div style={{
                fontFamily: "Arsenal, sans-serif",
                fontWeight: 700,
                fontStyle: "normal",
                letterSpacing: "-.04em",
                fontSize: "50px",
                lineHeight: 1,
                width: "fit-content",
              }}>
                {i18nextTranslate(i18nextKeys.customInvestmentsHeading)}
                <div
                  className='h-1 ml-32 mt-10 bg-black'
                  style={{ width: "36%" }}
                ></div>
              </div>
            </div>
            {isLoading ? (
              <div
                className="flex justify-center px-32 py-24"
                style={{ maxWidth: "940px" }}
              >
                <LoadingSpinner />
              </div>
            ) : assets.length ? (
              <>
                <div
                  className="grid row-gap-32 col-gap-40"
                  style={{
                    gridTemplateColumns: sm
                      ? `repeat(auto-fit, ${GRID_ITEM_WIDTH}px)`
                      : 'repeat(auto-fit, 100%)',
                    gridTemplateRows: 'auto auto auto auto',
                    gap: `${GRID_GAP}px`,
                    placeItems: sm ? 'start' : 'center',
                  }}
                >
                  {assets.map((assetConfig, index) => (
                    <InvestmentCard
                      key={assetConfig.Id + index}
                      dataQa={`purchase-assetCard-${(index + 1)}`}
                      assetConfig={assetConfig}
                    />
                  ))}
                </div>
              </>
              ) : <EmptyState style={{ maxWidth: "940px" }} />}
          </div>
          <CustomFooter />
        </div>
      </LogOnMount>
    </Amplitude>
  );
};

export default Investments;
